import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi, normalizeApiAssetPath } from "../api/client";
import QuotationPrintView, { QuotationPrintHeaderDraft } from "../components/QuotationPrintView";

const defaultAdjustmentsTitle = "Event Manager Charges";
const ganpatiQuotationType = "Ganpati Bappa Decoration";
const weddingQuotationType = "Wedding";

function normalizeAdjustmentsTitle(value?: unknown) {
  const title = String(value || "").trim();
  return !title || title === "Additional Charges / Adjustments" ? defaultAdjustmentsTitle : title;
}

function parsePayload(value: unknown) {
  if (!value) {
    return {};
  }

  if (typeof value === "string") {
    try {
      return JSON.parse(value) as Record<string, any>;
    } catch (error) {
      return {};
    }
  }

  return value as Record<string, any>;
}

function formatDateInput(value?: string | null) {
  return value ? String(value).slice(0, 10) : "";
}

function getTodayDateInput() {
  const today = new Date();
  const localDate = new Date(today.getTime() - today.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
}

function normalizePaymentTransactions(value: unknown): QuotationPrintHeaderDraft["paymentTransactions"] {
  const rows = Array.isArray(value) ? value : [];

  return rows.map((row: any) => ({
    paymentMethod: ["Cash", "UPI", "Bank Transfer", "Other"].includes(row.paymentMethod || row.payment_method) ? row.paymentMethod || row.payment_method : "Cash",
    amountPaid: Number(row.amountPaid ?? row.amount_paid ?? row.amount ?? 0),
    paymentDate: formatDateInput(row.paymentDate || row.payment_date) || formatDateInput(new Date().toISOString()),
    referenceNumber: String(row.referenceNumber || row.reference_number || "").trim()
  }));
}

function getPaymentTransactionsFromRecord(record: any, payload: Record<string, any>) {
  return normalizePaymentTransactions(
    payload.draft?.paymentTransactions ||
      payload.paymentTransactions ||
      payload.payment_transactions ||
      parsePayload(record.payment_transactions)
  );
}

function createDefaultNotes(): QuotationPrintHeaderDraft["notes"] {
  return [
    {
      title: "Additional Costs",
      icon: "",
      isVisible: true,
      points: [
        { pointText: "Diesel for all generators - charged at actual rates", isHighlighted: false },
        { pointText: "Food cost for security and valet drivers - extra", isHighlighted: false },
        { pointText: "Technical rider for artists (if not finalized) - extra", isHighlighted: false },
        { pointText: "Any items not listed here - charged separately", isHighlighted: false }
      ]
    },
    {
      title: "Setup Requirements",
      icon: "",
      isVisible: true,
      points: [
        { pointText: "Venue access required minimum 2 days prior to event", isHighlighted: false },
        { pointText: "All decorative elements will be themed for consistency", isHighlighted: false },
        { pointText: "Complete setup within allotted access time", isHighlighted: false }
      ]
    },
    {
      title: "Client Responsibilities",
      icon: "",
      isVisible: true,
      points: [
        { pointText: "All permits and permissions (fireworks, sound, parking, venue)", isHighlighted: false },
        { pointText: "Weather contingency planning", isHighlighted: false },
        { pointText: "Final headcount confirmation before event", isHighlighted: false }
      ]
    },
    {
      title: "Decor Specification",
      icon: "",
      isVisible: true,
      points: [
        { pointText: "All floral decorations - 100% Artificial Flowers", isHighlighted: false },
        { pointText: "Consistent theme across all decor elements", isHighlighted: false },
        { pointText: "Premium quality artificial floral arrangements", isHighlighted: false }
      ]
    }
  ];
}

function createGanpatiDefaultNotes(): QuotationPrintHeaderDraft["notes"] {
  return [
    {
      title: "Limited Setups - First Come, First Served",
      icon: "",
      isVisible: true,
      points: [{ pointText: "We have a limited number of decoration setups available. Priority will be given to customers who book early.", isHighlighted: false }]
    },
    {
      title: "Full Payment Required at the Time of Booking",
      icon: "",
      isVisible: true,
      points: [{ pointText: "To confirm the booking, 100% payment is mandatory. Partial payments will not be accepted for reserving the setup.", isHighlighted: false }]
    },
    {
      title: "No Cancellation Policy",
      icon: "",
      isVisible: true,
      points: [{ pointText: "Once the booking is confirmed and payment has been made, cancellation will not be permitted under any circumstances. Please make the booking only after receiving full confirmation from the client.", isHighlighted: false }]
    },
    {
      title: "No Unauthorized Modifications",
      icon: "",
      isVisible: true,
      points: [{ pointText: "The setup must not be altered, painted, or additionally decorated without prior approval from Eventena. Any unauthorized modifications, damage, or mishandling will be chargeable.", isHighlighted: false }]
    },
    {
      title: "Photography and Videography Permission",
      icon: "",
      isVisible: true,
      points: [{ pointText: "Eventena reserves the right to take photographs or create video content for marketing purposes unless the client requests privacy in advance.", isHighlighted: false }]
    },
    {
      title: "Emergency Contact and Support",
      icon: "",
      isVisible: true,
      points: [{ pointText: "For any issues during the setup or rental period, the client may contact our dedicated support number: Eventena Helpline: 9405076050", isHighlighted: false }]
    }
  ];
}

function createDefaultPaymentMilestones(): QuotationPrintHeaderDraft["paymentMilestones"] {
  return [
    {
      milestoneLabel: "At Booking",
      percentage: 50,
      dueRuleText: "",
      dueDate: "",
      amount: 0,
      description: "Advance to confirm booking and reserve all services",
      paymentStatus: "pending",
      isVisible: true
    },
    {
      milestoneLabel: "1 Week Before Event",
      percentage: 30,
      dueRuleText: "",
      dueDate: "",
      amount: 0,
      description: "Second installment 7 days prior to event date",
      paymentStatus: "pending",
      isVisible: true
    },
    {
      milestoneLabel: "Day After Event",
      percentage: 20,
      dueRuleText: "",
      dueDate: "",
      amount: 0,
      description: "Final balance cleared the next day after event",
      paymentStatus: "pending",
      isVisible: true
    }
  ];
}

function createDefaultTerms(): QuotationPrintHeaderDraft["terms"] {
  return [
    "Venue must be available 1 day prior for setup",
    "Event company not liable for permission-related issues",
    "Equipment damage due to weather charged extra",
    "Quantity / quality changes affect final billing",
    "Advance payment forfeited upon cancellation",
    "Early cancellation terms are negotiable",
    "Client receives digital copies of event setup documentation",
    "Client responsible for all required permissions and licenses",
    "Natural calamities (rain, storms) - company not responsible",
    "Items not in quotation will be charged extra",
    "Last-minute additions subject to availability",
    "Cancellation within 15 days = 75% of total amount charged",
    "Company reserves rights to use event photos for promotion",
    "This quotation is customizable per specific requirements and theme preferences"
  ].map((termText) => ({
    termText,
    isMandatory: true,
    isVisible: true
  }));
}

function isEnabled(value: unknown) {
  return value === undefined || value === null || value === true || value === 1;
}

function calculateDraftItemTotal(item: any) {
  if (item.lineTotal !== undefined && item.lineTotal !== null) {
    return Number(item.lineTotal || 0);
  }

  if (item.line_total !== undefined && item.line_total !== null) {
    return Number(item.line_total || 0);
  }

  const quantity = Number(item.quantity || 0);
  const unitRate = Number(item.unitRate ?? item.unit_rate ?? 0);
  const pricingMode = item.pricingMode || item.pricing_mode || "manual";
  const base = pricingMode === "quantity_x_rate" ? quantity * unitRate : unitRate;
  const discountType = item.discountType || item.discount_type || "none";
  const discountValue = Number(item.discountValue ?? item.discount_value ?? 0);
  const discount = discountType === "percent" ? (base * discountValue) / 100 : discountType === "flat" ? discountValue : 0;
  const taxable = Math.max(base - discount, 0);
  return taxable + (taxable * Number(item.taxRate ?? item.tax_rate ?? 0)) / 100;
}

function calculateDraftSubtotal(sections: any[]) {
  return (sections || []).reduce((sectionSum, section) => {
    if (!isEnabled(section.isVisible ?? section.is_visible)) {
      return sectionSum;
    }

    return sectionSum + (section.items || []).reduce((itemSum: number, item: any) => {
      return itemSum + (isEnabled(item.includeInTotal ?? item.include_in_total) && isEnabled(item.isVisible ?? item.is_visible) ? calculateDraftItemTotal(item) : 0);
    }, 0);
  }, 0);
}

function calculateDraftAdjustmentAmount(adjustment: any, subtotal: number) {
  if (adjustment.amount !== undefined && adjustment.amount !== null) {
    return Number(adjustment.amount || 0);
  }

  const calculationType = adjustment.calculationType || adjustment.calculation_type || "fixed";
  const calculationValue = Number(adjustment.calculationValue ?? adjustment.calculation_value ?? adjustment.amount ?? 0);
  return calculationType === "percent" ? (subtotal * calculationValue) / 100 : calculationValue;
}

function calculateDraftTotal(draft: QuotationPrintHeaderDraft) {
  const subtotal = calculateDraftSubtotal(draft.sections || []);

  const adjustments = draft.showAdjustments
    ? (draft.adjustments || []).reduce((sum, adjustment) => {
        if (!isEnabled(adjustment.isVisible)) {
          return sum;
        }

        return sum + calculateDraftAdjustmentAmount(adjustment, subtotal);
      }, 0)
    : 0;

  return Math.max(subtotal + adjustments, 0);
}

function getPaymentDbFields(draft: QuotationPrintHeaderDraft) {
  const isPartialPayment = draft.paymentMethod === "Partial Paid" || draft.paymentMethod === "Paid";
  const paymentTransactions = normalizePaymentTransactions(draft.paymentTransactions);
  const transactionPaidTotal = paymentTransactions.reduce((sum, transaction) => sum + Number(transaction.amountPaid || 0), 0);
  const partialTotalAmount = isPartialPayment ? Number(draft.partialTotalAmount || 0) : 0;
  const partialAmountPaid = isPartialPayment ? transactionPaidTotal : 0;
  const remainingBalance = Math.max(partialTotalAmount - partialAmountPaid, 0);

  return {
    client_mobile: String(draft.clientMobile || "").replace(/\D/g, ""),
    quotation_type: draft.quotationType || ganpatiQuotationType,
    payment_method: isPartialPayment && partialTotalAmount > 0 && remainingBalance <= 0 ? "Paid" : draft.paymentMethod || "Cash",
    partial_total_amount: partialTotalAmount,
    partial_amount_paid: partialAmountPaid,
    remaining_balance: remainingBalance,
    payment_transactions: paymentTransactions
  };
}

function getStatusForPayment(draftStatus: string, paymentMethod: string) {
  if (paymentMethod === "Paid") {
    return "granted";
  }

  if (paymentMethod === "Partial Paid") {
    return "pending";
  }

  return draftStatus || "pending";
}

function normalizeLegalTextForCompare(notes: QuotationPrintHeaderDraft["notes"], terms: QuotationPrintHeaderDraft["terms"]) {
  return JSON.stringify({
    notes: (notes || []).map((note) => ({
      title: note.title || "",
      points: (note.points || []).map((point) => point.pointText || "")
    })),
    terms: (terms || []).map((term) => term.termText || "")
  });
}

function hasDefaultLegalText(draft: QuotationPrintHeaderDraft) {
  const current = normalizeLegalTextForCompare(draft.notes, draft.terms);
  return (
    current === normalizeLegalTextForCompare(createDefaultNotes(), createDefaultTerms()) ||
    current === normalizeLegalTextForCompare(createGanpatiDefaultNotes(), createGanpatiDefaultTerms())
  );
}

function createGanpatiDefaultTerms(): QuotationPrintHeaderDraft["terms"] {
  return [
    "Transportation Responsibility and Damage Clause: If transportation is arranged by the customer, the setup must be returned in the same condition in which it was provided. Any damage, breakage, or missing parts will be chargeable, and the applicable cost will be recovered from the customer.",
    "Installation Timeline: The booked decoration setup will be installed at the client's location between two and five days before Ganesh Chaturthi. The installation date and time will be communicated in advance.",
    "Changes to the Setup After Booking: Any required modifications or changes to the booked setup must be communicated at least 20 days before Ganesh Chaturthi. No changes will be accepted after this period because of planned logistics and execution schedules.",
    "Setup Usage Duration: The decoration setup will be provided for the fixed Ganesh Chaturthi festival duration, from a minimum of one day to a maximum of 11 days. Late returns or extended use without prior permission will incur additional charges.",
    "Location Accessibility for Installation: The client must ensure that the installation and dismantling location is easily accessible. Any delays or difficulties caused by accessibility issues will be the client's responsibility.",
    "Basic Requirements: The client must ensure that a clean and safe space is available at the setup location. Eventena will not be responsible for local restrictions, disturbances, or issues at the venue.",
    "Security Responsibility: Once the setup has been installed at the client's location, the client will be responsible for its safety and security. Eventena will not be liable for any loss, theft, damage caused by third parties, or damage caused by weather conditions.",
    "Service Area Limitation - Kolhapur City Only: Due to the high volume of bookings during Ganesh Chaturthi, decoration setup services will be available only within Kolhapur city limits. Services will not be provided outside Kolhapur city.",
    "All confirmed setups will be installed between three and five days before Ganesh Chaturthi according to the schedule shared by the Eventena team."
  ].map((termText) => ({
    termText,
    isMandatory: true,
    isVisible: true
  }));
}

function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read QR code image"));
    reader.readAsDataURL(file);
  });
}

function createDefaultDraft(): QuotationPrintHeaderDraft {
  return {
    quoteNumber: "",
    issuedByLabel: "Eventena",
    status: "pending",
    title: "Custom Event Quotation",
    clientMobile: "",
    quotationType: ganpatiQuotationType,
    headerTagline: "Crafted with care - Executed with excellence",
    eventFamily: "",
    eventGroom: "",
    eventBride: "",
    eventDate: getTodayDateInput(),
    eventVenue: "",
    eventOccasion: "Custom Event",
    eventDecorType: "",
    paymentQrTitle: "Scan to Pay",
    paymentQrNote: "Use this QR code for booking and milestone payments.",
    paymentQrImageUrl: "",
    footerTagline: "Creating unforgettable moments with professional excellence",
    footerPhone: "Mobile: +919405076050 +917972426493",
    totalNote: "Exclusive of diesel, food & extra permissions",
    showItemPrice: false,
    showItemAmount: false,
    showAdjustments: false,
    adjustmentsTitle: defaultAdjustmentsTitle,
    showAdjustmentPercentage: true,
    showAdjustmentAmount: true,
    showPaymentDetails: true,
    showScanToPay: true,
    paymentMethod: "Cash",
    partialTotalAmount: 0,
    partialAmountPaid: 0,
    partialTotalManual: false,
    paymentTransactions: [],
    sections: [
      {
        sectionCode: "01",
        sectionTitle: "Custom Services",
        sectionDescription: "",
        isVisible: true,
        isOptional: false,
        showSectionTotal: false,
        sectionNote: "",
        items: [
          {
            itemCode: "01",
            itemName: "Custom Item",
            itemDescription: "",
            quantity: 1,
            unit: "",
            dimensionText: "",
            pricingMode: "manual",
            unitRate: 0,
            lineTotal: 0,
            discountType: "none",
            discountValue: 0,
            taxRate: 0,
            isOptional: false,
            includeInTotal: true,
            isVisible: true,
            remarks: ""
          }
        ]
      }
    ],
    notes: createGanpatiDefaultNotes(),
    paymentMilestones: createDefaultPaymentMilestones(),
    terms: createGanpatiDefaultTerms(),
    adjustments: []
  };
}

function mergeDraftWithDefaults(value: Record<string, any>): QuotationPrintHeaderDraft {
  const defaults = createDefaultDraft();
  const draft = {
    ...defaults,
    ...value
  };

  return {
    ...draft,
    sections: Array.isArray(draft.sections) && draft.sections.length ? draft.sections : defaults.sections,
    notes: Array.isArray(draft.notes) && draft.notes.length ? draft.notes : defaults.notes,
    paymentMilestones: Array.isArray(draft.paymentMilestones) && draft.paymentMilestones.length ? draft.paymentMilestones : defaults.paymentMilestones,
    terms: Array.isArray(draft.terms) && draft.terms.length ? draft.terms : defaults.terms,
    adjustments: Array.isArray(draft.adjustments) ? draft.adjustments : defaults.adjustments,
    eventDate: draft.eventDate || defaults.eventDate
  };
}

function createQuotationFromDraft(draft: QuotationPrintHeaderDraft) {
  return {
    quote_number: draft.quoteNumber,
    status: draft.status,
    title: draft.title,
    header_tagline: draft.headerTagline,
    footer_tagline: draft.footerTagline,
    issued_by_label: draft.issuedByLabel,
    currency_code: "INR",
    show_item_price: draft.showItemPrice,
    show_item_amount: draft.showItemAmount,
    quote_discount_total: 0,
    round_off_amount: 0,
    total_note: draft.totalNote || "Exclusive of diesel, food & extra permissions",
    header_config: {
      client_mobile: draft.clientMobile,
      quotation_type: draft.quotationType,
      quotation_event_kind: draft.eventOccasion,
      event_host_family_name: draft.eventFamily,
      event_groom_name: draft.eventGroom,
      event_bride_name: draft.eventBride,
      event_start_at: draft.eventDate,
      event_venue: draft.eventVenue,
      event_occasion: draft.eventOccasion,
      event_decor_type_label: draft.eventDecorType
    },
    footer_config: {
      payment_qr_title: draft.paymentQrTitle,
      payment_qr_note: draft.paymentQrNote,
      payment_qr_image_url: draft.paymentQrImageUrl || "",
      show_scan_to_pay: draft.showScanToPay !== false,
      footer_phone: draft.footerPhone
    },
    payment_method: draft.paymentMethod,
    partial_total_amount: getPaymentDbFields(draft).partial_total_amount,
    partial_amount_paid: getPaymentDbFields(draft).partial_amount_paid,
    remaining_balance: getPaymentDbFields(draft).remaining_balance,
    payment_transactions: getPaymentDbFields(draft).payment_transactions,
    sections: draft.sections,
    notes: draft.notes,
    paymentMilestones: draft.paymentMilestones,
    terms: draft.terms,
    adjustments: draft.adjustments
  };
}

export default function CustomQuotationEditorPage({ printOnly = false }: { printOnly?: boolean }) {
  const { customQuotationId } = useParams();
  const navigate = useNavigate();
  const [savedCustomQuotationId, setSavedCustomQuotationId] = useState<string | null>(customQuotationId || null);
  const activeCustomQuotationId = customQuotationId || savedCustomQuotationId;
  const isEditMode = Boolean(activeCustomQuotationId);
  const [draft, setDraft] = useState<QuotationPrintHeaderDraft>(createDefaultDraft());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paymentQrUploading, setPaymentQrUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    const loadCustomQuotation = async () => {
      if (!customQuotationId) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const record = await adminApi.getResourceById<any>("custom-quotations", customQuotationId);
        const payload = parsePayload(record.payload);
        setDraft(mergeDraftWithDefaults({
          ...(payload.draft || payload),
          quoteNumber: payload.draft?.quoteNumber || payload.quoteNumber || record.quote_number || "",
          title: payload.draft?.title || payload.title || record.title || "Custom Event Quotation",
          status: payload.draft?.status || payload.status || record.status || "draft",
          clientMobile: payload.draft?.clientMobile || payload.clientMobile || payload.client_mobile || record.client_mobile || "",
          quotationType: payload.draft?.quotationType || payload.quotationType || payload.quotation_type || record.quotation_type || ganpatiQuotationType,
          paymentMethod: payload.draft?.paymentMethod || payload.paymentMethod || payload.payment_method || record.payment_method || "Cash",
          partialTotalAmount: Number(payload.draft?.partialTotalAmount ?? payload.partialTotalAmount ?? payload.partial_total_amount ?? record.partial_total_amount ?? 0),
          partialAmountPaid: Number(payload.draft?.partialAmountPaid ?? payload.partialAmountPaid ?? payload.partial_amount_paid ?? record.partial_amount_paid ?? 0),
          paymentTransactions: getPaymentTransactionsFromRecord(record, payload),
          totalNote: payload.draft?.totalNote || payload.totalNote || payload.total_note || "Exclusive of diesel, food & extra permissions",
          paymentQrImageUrl: payload.draft?.paymentQrImageUrl || payload.paymentQrImageUrl || payload.footer_config?.payment_qr_image_url || "",
          showItemPrice: printOnly ? payload.draft?.showItemPrice ?? payload.showItemPrice ?? payload.show_item_price ?? record.show_item_price ?? false : false,
          showItemAmount: printOnly ? payload.draft?.showItemAmount ?? payload.showItemAmount ?? payload.show_item_amount ?? record.show_item_amount ?? false : false,
          showAdjustments: printOnly ? payload.draft?.showAdjustments ?? payload.showAdjustments ?? false : false,
          adjustmentsTitle: normalizeAdjustmentsTitle(payload.draft?.adjustmentsTitle || payload.adjustmentsTitle),
          showAdjustmentPercentage: payload.draft?.showAdjustmentPercentage ?? payload.showAdjustmentPercentage ?? true,
          showAdjustmentAmount: payload.draft?.showAdjustmentAmount ?? payload.showAdjustmentAmount ?? true,
          showPaymentDetails: payload.draft?.showPaymentDetails ?? payload.showPaymentDetails ?? true,
          showScanToPay: payload.draft?.showScanToPay ?? payload.showScanToPay ?? payload.footer_config?.show_scan_to_pay ?? true
        }));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load custom quotation");
      } finally {
        setLoading(false);
      }
    };

    void loadCustomQuotation();
  }, [customQuotationId]);

  const quotation = useMemo(() => createQuotationFromDraft(draft), [draft]);

  useEffect(() => {
    if ((draft.paymentMethod !== "Partial Paid" && draft.paymentMethod !== "Paid") || draft.partialTotalManual) {
      return;
    }

    const calculatedTotal = calculateDraftTotal(draft);
    if (Number(draft.partialTotalAmount || 0) === calculatedTotal) {
      return;
    }

    setDraft((current) => ({
      ...current,
      partialTotalAmount: calculatedTotal
    }));
  }, [draft]);

  useEffect(() => {
    if (draft.paymentMethod !== "Partial Paid" && draft.paymentMethod !== "Paid") {
      return;
    }

    const transactions = normalizePaymentTransactions(draft.paymentTransactions);
    const paidTotal = transactions.reduce((sum, transaction) => sum + Number(transaction.amountPaid || 0), 0);
    const totalAmount = Number(draft.partialTotalAmount || 0);
    const nextPaymentMethod = totalAmount > 0 && paidTotal >= totalAmount ? "Paid" : "Partial Paid";
    const nextStatus = getStatusForPayment(draft.status, nextPaymentMethod);

    if (Number(draft.partialAmountPaid || 0) === paidTotal && draft.paymentMethod === nextPaymentMethod && draft.status === nextStatus) {
      return;
    }

    setDraft((current) => ({
      ...current,
      paymentMethod: nextPaymentMethod,
      partialAmountPaid: paidTotal,
      status: nextStatus
    }));
  }, [draft]);

  const handleDraftChange = (key: keyof QuotationPrintHeaderDraft, value: any) => {
    if (key === "quotationType") {
      setDraft((current) => {
        const nextQuotationType = String(value || "").trim();

        if (nextQuotationType !== weddingQuotationType && nextQuotationType !== ganpatiQuotationType) {
          return {
            ...current,
            quotationType: nextQuotationType
          };
        }

        const canReplaceLegalText =
          hasDefaultLegalText(current) ||
          window.confirm("Quotation type badaltana current Important Notes and Terms & Conditions replace hotil. Continue?");

        if (!canReplaceLegalText) {
          return current;
        }

        return {
          ...current,
          quotationType: nextQuotationType,
          notes: nextQuotationType === weddingQuotationType ? createDefaultNotes() : createGanpatiDefaultNotes(),
          terms: nextQuotationType === weddingQuotationType ? createDefaultTerms() : createGanpatiDefaultTerms()
        };
      });
      return;
    }

    if (key === "paymentMethod") {
      setDraft((current) => ({
        ...current,
        paymentMethod: value,
        partialTotalAmount: (value === "Partial Paid" || value === "Paid") && !current.partialTotalManual ? calculateDraftTotal(current) : current.partialTotalAmount,
        paymentTransactions: value === "Partial Paid" || value === "Paid" ? current.paymentTransactions : [],
        partialAmountPaid: value === "Partial Paid" || value === "Paid" ? current.partialAmountPaid : 0
      }));
      return;
    }

    if (key === "partialTotalAmount") {
      setDraft((current) => ({
        ...current,
        partialTotalAmount: value,
        partialTotalManual: true
      }));
      return;
    }

    setDraft((current) => ({
      ...current,
      [key]: value
    }));
  };

  const handlePaymentQrUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file for the QR code");
      return;
    }

    try {
      setPaymentQrUploading(true);
      setError(null);
      const dataUrl = await readFileAsDataUrl(file);
      const uploaded = await adminApi.post<{ imageUrl: string; path?: string; payment_qr_image_url?: string }>("/quotations/payment-qr-upload", {
        filename: file.name,
        mimeType: file.type,
        dataUrl
      });
      handleDraftChange("paymentQrImageUrl", normalizeApiAssetPath(uploaded.payment_qr_image_url || uploaded.path || uploaded.imageUrl));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload QR code image");
    } finally {
      setPaymentQrUploading(false);
    }
  };

  const saveCustomQuotation = async () => {
    try {
      setSaving(true);
      setError(null);
      setSaveMessage(null);
      const normalizedMobile = String(draft.clientMobile || "").replace(/\D/g, "");
      if (!/^\d{10}$/.test(normalizedMobile)) {
        throw new Error("Please enter a valid 10-digit client mobile number");
      }

      if (draft.paymentMethod === "Partial Paid" || draft.paymentMethod === "Paid") {
        const totalAmount = Number(draft.partialTotalAmount || 0);
        const paymentTransactions = normalizePaymentTransactions(draft.paymentTransactions);
        const amountPaid = paymentTransactions.reduce((sum, transaction) => sum + Number(transaction.amountPaid || 0), 0);
        if (totalAmount <= 0) {
          throw new Error("Please enter the total quotation amount for partial payment");
        }
        if (!paymentTransactions.length) {
          throw new Error("Please add at least one partial payment transaction");
        }
        const invalidTransaction = paymentTransactions.find((transaction) => Number(transaction.amountPaid || 0) <= 0 || !transaction.paymentDate);
        if (invalidTransaction) {
          throw new Error("Each partial payment must have a valid amount and payment date");
        }
        if (amountPaid < 0 || amountPaid > totalAmount) {
          throw new Error("Amount paid must be between 0 and the total quotation amount");
        }
      }

      const draftForSave = {
        ...draft,
        clientMobile: normalizedMobile,
        quoteNumber: activeCustomQuotationId ? draft.quoteNumber : ""
      };
      const paymentDbFields = getPaymentDbFields(draftForSave);
      const nextStatus = getStatusForPayment(draftForSave.status, paymentDbFields.payment_method);
      const normalizedDraftForSave = {
        ...draftForSave,
        status: nextStatus,
        paymentMethod: paymentDbFields.payment_method,
        partialAmountPaid: paymentDbFields.partial_amount_paid,
        paymentTransactions: paymentDbFields.payment_transactions
      };
      const payload = {
        ...(activeCustomQuotationId ? { quote_number: draft.quoteNumber || null } : {}),
        title: draft.title || "Custom Event Quotation",
        status: nextStatus,
        ...paymentDbFields,
        payload: { draft: normalizedDraftForSave }
      };

      if (activeCustomQuotationId) {
        const updated = await adminApi.updateResource<any>("custom-quotations", activeCustomQuotationId, payload);
        const updatedPayload = parsePayload(updated.payload);
        setDraft(mergeDraftWithDefaults({
          ...(updatedPayload.draft || updatedPayload || normalizedDraftForSave),
          quoteNumber: updatedPayload.draft?.quoteNumber || updatedPayload.quoteNumber || updated.quote_number || draft.quoteNumber,
          title: updatedPayload.draft?.title || updatedPayload.title || updated.title || draft.title,
          status: updatedPayload.draft?.status || updatedPayload.status || updated.status || draft.status
        }));
        navigate(`/admin/custom-quotations/${activeCustomQuotationId}/print`);
      } else {
        const created = await adminApi.createResource<{ id?: number; quote_number?: string }>("custom-quotations", payload);
        if (!created.id) {
          throw new Error("Custom quotation was created without an id");
        }

        const createdRecord = await adminApi.getResourceById<any>("custom-quotations", created.id);
        const generatedQuoteNumber = created.quote_number || createdRecord.quote_number || "";
        const nextDraft = { ...normalizedDraftForSave, quoteNumber: generatedQuoteNumber };
        const updated = await adminApi.updateResource<any>("custom-quotations", created.id, {
          quote_number: generatedQuoteNumber || null,
          title: draft.title || "Custom Event Quotation",
          status: nextStatus,
          ...getPaymentDbFields(nextDraft),
          payload: { draft: nextDraft }
        });
        const updatedPayload = parsePayload(updated.payload);
        setDraft(mergeDraftWithDefaults({
          ...(updatedPayload.draft || updatedPayload || nextDraft),
          quoteNumber: updatedPayload.draft?.quoteNumber || updatedPayload.quoteNumber || updated.quote_number || generatedQuoteNumber,
          title: updatedPayload.draft?.title || updatedPayload.title || updated.title || draft.title,
          status: updatedPayload.draft?.status || updatedPayload.status || updated.status || draft.status
        }));
        setSavedCustomQuotationId(String(created.id));
        navigate(`/admin/custom-quotations/${created.id}/print`);
      }
      setSaveMessage("Custom quotation saved successfully.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save custom quotation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Paper elevation={0} className="print-hidden" sx={{ p: 2.5, borderRadius: 4, border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 250, 244, 0.94)" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
          <div>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {printOnly ? "Custom Quotation Print View" : isEditMode ? "Edit Custom Quotation" : "Create Custom Quotation"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Uses the same print-view fields and inline editing style as the standard quotation print view.
            </Typography>
          </div>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="outlined" startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/admin/custom-quotations")}>
              Back
            </Button>
            {printOnly ? (
              <>
                <Button variant="outlined" onClick={() => navigate(`/admin/custom-quotations/${customQuotationId}/edit`)}>
                  Edit
                </Button>
                <Button variant="contained" startIcon={<PrintRoundedIcon />} onClick={() => window.print()}>
                  Print
                </Button>
              </>
            ) : (
              <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={() => void saveCustomQuotation()} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress />
        </Stack>
      ) : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      {saveMessage ? <Alert severity="success">{saveMessage}</Alert> : null}

      {!loading ? (
        <QuotationPrintView
          quotation={quotation}
          editing={!printOnly}
          draft={draft}
          onDraftChange={handleDraftChange}
          onPaymentQrUpload={!printOnly ? handlePaymentQrUpload : undefined}
          onPaymentQrUseDefault={!printOnly ? () => handleDraftChange("paymentQrImageUrl", "") : undefined}
          paymentQrUploading={paymentQrUploading}
          enableColumnVisibilityControls
          quoteNumberReadOnly
        />
      ) : null}

      {!printOnly ? (
        <Paper elevation={0} className="custom-quote-sticky-save print-hidden">
          <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={() => void saveCustomQuotation()} disabled={saving}>
            {saving ? "Saving..." : "Save Custom Quotation"}
          </Button>
        </Paper>
      ) : null}
    </Stack>
  );
}
